import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Mail, Lock, Eye, EyeOff, User, Loader2, Check, Shield, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

function extractErrorMessage(err) {
  // 1. Backend returned a response with detail
  if (err.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((e) => {
          if (e && typeof e.msg === 'string') {
            let msg = e.msg;
            if (msg.startsWith('Value error, ')) msg = msg.slice(13);
            return msg;
          }
          return JSON.stringify(e);
        })
        .filter(Boolean)
        .join('. ');
    }
    if (detail && typeof detail.msg === 'string') return detail.msg;
    return String(detail);
  }

  // 2. Backend returned a response with message
  if (err.response?.data?.message) return err.response.data.message;

  // 3. Backend returned a status but no detail (e.g. 500 without body)
  if (err.response?.status) {
    const status = err.response.status;
    if (status === 409) return 'This email is already registered.';
    if (status === 422) return 'Please check your input and try again.';
    if (status === 429) return 'Too many attempts. Please wait a few minutes.';
    if (status >= 500) return 'Server error. Please try again in a moment.';
    return `Request failed (${status}). Please try again.`;
  }

  // 4. Network error (no response at all — CORS, timeout, offline)
  if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
    return 'Unable to connect to the server. Please try again in a moment.';
  }
  if (err.code === 'ECONNABORTED') {
    return 'Request timed out. Please try again.';
  }

  // 5. Fallback
  return err.message || 'Something went wrong. Please try again.';
}

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isOver18, setIsOver18] = useState(false);

  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const passwordRequirements = [
    { met: password.length >= 8, text: t('auth.password_req_length', 'At least 8 characters') },
    { met: /[A-Z]/.test(password), text: t('auth.password_req_upper', 'One uppercase letter') },
    { met: /[0-9]/.test(password), text: t('auth.password_req_number', 'One number') },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Client-side validation
    if (!name.trim()) {
      setError(t('auth.error_name_required', 'Please enter your name.'));
      return;
    }

    if (!agreedToTerms || !isOver18) {
      setError(t('auth.error_terms', 'Please agree to the terms and confirm you are 18+.'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.error_password_mismatch', 'Passwords do not match.'));
      return;
    }

    if (!passwordRequirements.every(req => req.met)) {
      setError(t('auth.error_password_weak', 'Please meet all password requirements.'));
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name);
      toast.success(t('auth.account_created', 'Account created successfully!'), {
        icon: <Check className="w-4 h-4 text-[#2D6A4F]" />
      });
      navigate('/onboarding');
    } catch (err) {
      const message = extractErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex">
      {/* Left Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1555881400-74d7acaacd8b?w=1200')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#6E1A24]/60 to-[#C97A63]/40" />
        <div className="absolute inset-0 flex flex-col justify-end p-16">
          <div className="max-w-md">
            <p className="text-3xl font-serif font-light text-white leading-relaxed mb-6">
              "LusoLove helped me find someone who shares my Portuguese roots. We're getting married next summer!"
            </p>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm overflow-hidden">
                <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop" alt="" className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="text-white font-medium">João Ferreira</p>
                <p className="text-white/70 text-sm">Zurich, Switzerland</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 overflow-y-auto">
        <div className="w-full max-w-md animate-fade-in">
          <Link to="/" className="flex items-center gap-3 mb-10 group" data-testid="register-logo">
            <Heart className="w-9 h-9 text-[#6E1A24] transition-transform group-hover:scale-110" strokeWidth={1.5} fill="currentColor" fillOpacity={0.1} />
            <span className="text-2xl font-serif font-medium text-[#2D2D2A]">LusoLove</span>
          </Link>

          <h1 className="text-4xl font-serif font-light text-[#2D2D2A] mb-3">{t('auth.register_title', 'Create your profile')}</h1>
          <p className="text-lg text-[#6C6C67] font-light mb-6">
            {t('auth.register_subtitle', 'Join Portuguese singles across Europe')}
          </p>

          {/* Trust signals */}
          <div className="flex items-center gap-6 mb-8 text-sm text-[#6C6C67]">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{t('landing.cta_2min', 'Takes 2 min')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#2D6A4F]" />
              <span>{t('landing.trust_verified', 'Verified profiles')}</span>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-sm font-medium text-[#2D2D2A] block mb-2">{t('auth.name', 'Full Name')}</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6C6C67]" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-white border border-[#E5E3DB] rounded-2xl focus:outline-none focus:border-[#6E1A24] focus:ring-4 focus:ring-[#6E1A24]/5 transition-all"
                  placeholder={t('auth.name_placeholder', 'Your name')}
                  required
                  data-testid="register-name"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-[#2D2D2A] block mb-2">{t('auth.email', 'Email')}</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6C6C67]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-white border border-[#E5E3DB] rounded-2xl focus:outline-none focus:border-[#6E1A24] focus:ring-4 focus:ring-[#6E1A24]/5 transition-all"
                  placeholder="your@email.com"
                  required
                  data-testid="register-email"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-[#2D2D2A] block mb-2">{t('auth.password', 'Password')}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6C6C67]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 bg-white border border-[#E5E3DB] rounded-xl focus:outline-none focus:border-[#6E1A24] transition-colors"
                  placeholder="••••••••"
                  required
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6C6C67] hover:text-[#2D2D2A]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              <div className="mt-3 space-y-1">
                {passwordRequirements.map((req) => (
                  <div key={req.text} className={`flex items-center gap-2 text-sm ${req.met ? 'text-[#2D6A4F]' : 'text-[#6C6C67]'}`}>
                    <Check className={`w-4 h-4 ${req.met ? 'opacity-100' : 'opacity-30'}`} />
                    {req.text}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-[#2D2D2A] block mb-2">{t('auth.confirm_password', 'Confirm Password')}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6C6C67]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white border border-[#E5E3DB] rounded-xl focus:outline-none focus:border-[#6E1A24] transition-colors"
                  placeholder="••••••••"
                  required
                  data-testid="register-confirm-password"
                />
              </div>
            </div>

            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isOver18}
                  onChange={(e) => setIsOver18(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-[#E5E3DB] text-[#6E1A24] focus:ring-[#6E1A24]"
                  data-testid="register-age-checkbox"
                />
                <span className="text-sm text-[#6C6C67]">
                  {t('auth.age_confirm', 'I confirm that I am 18 years or older')}
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-[#E5E3DB] text-[#6E1A24] focus:ring-[#6E1A24]"
                  data-testid="register-terms-checkbox"
                />
                <span className="text-sm text-[#6C6C67]">
                  {t('auth.terms_agree', 'I agree to the Terms of Service and Privacy Policy')}
                </span>
              </label>
            </div>

            <Button
              type="submit"
              className="bg-[#6E1A24] text-white rounded-2xl w-full py-4 font-semibold text-base hover:bg-[#8A2331] hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              disabled={loading || !agreedToTerms || !isOver18}
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('auth.creating_account', 'Creating account...')}
                </>
              ) : (
                t('auth.register_btn', 'Create Account')
              )}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-[#E5E3DB]" />
            <span className="text-sm text-[#6C6C67]">{t('common.or', 'or')}</span>
            <div className="flex-1 h-px bg-[#E5E3DB]" />
          </div>

          <Button
            type="button"
            onClick={googleLogin}
            className="w-full bg-white border border-[#E5E3DB] text-[#2D2D2A] rounded-xl py-3 font-medium hover:bg-[#F5F2EB] transition-colors flex items-center justify-center gap-3"
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('auth.register_google', 'Continue with Google')}
          </Button>

          <p className="mt-6 text-center text-sm text-[#6C6C67]">
            {t('auth.register_has_account', 'Already have an account?')}{' '}
            <Link to="/login" className="text-[#6E1A24] font-medium hover:underline" data-testid="login-link">
              {t('auth.register_login_link', 'Sign in')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
